import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { DatePicker, Empty, Select, Spin, Table } from 'antd-v5';
import StandardLayoutV2 from '../../../../Layout/StandardLayoutV2';
import AddRolexInquiryModal from '../../../../Rolex/AddRolexInquiryModal';
import { getVendorItemCollections } from '../../../../../api/vendors';
import { listInquiries } from '../../../../../api/vendors';
import { getMerchantTags } from '../../../../../services/clients';
import { transformInquiry } from '../../../../../components/Routes/Rolex/utils';
import useVendorByKey from '../../../../../hooks/query/useVendorByKey';

const { RangePicker } = DatePicker;

const STATUS_OPTIONS = [
  { label: 'All Statuses', value: '' },
  { label: 'Waiting', value: 'WAITING' },
  { label: 'Ordered', value: 'ORDERED' },
  { label: 'Cancelled', value: 'CANCELLED' },
  { label: 'Fulfilled', value: 'FULFILLED' },
];

const AllRolexInquiries = ({ auth }) => {
  const history = useHistory();
  const { vendorId: rolexVendorId } = useVendorByKey('rolex');
  const [collectionOptions, setCollectionOptions] = useState([]);
  const [collectionFilter, setCollectionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [rawData, setRawData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editingInquiry, setEditingInquiry] = useState(null);
  const [tagLookup, setTagLookup] = useState([]);

  useEffect(() => {
    if (!rolexVendorId) return;
    getVendorItemCollections([rolexVendorId])
      .then(response => {
        const options = [{ label: 'All Collections', value: '' }];
        (response?.data || []).forEach(item => {
          options.push({ label: item.name, value: item.name });
        });
        setCollectionOptions(options);
      })
      .catch(() => setCollectionOptions([]));
  }, [rolexVendorId]);

  useEffect(() => {
    getMerchantTags({ query: { limit: 500 }, loadingStatusOff: true })
      .then(response => setTagLookup(response?.results || []))
      .catch(() => {});
  }, []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const query = {
        vendorKey: 'rolex',
        associateId: auth?.userId,
        page,
        pageSize,
      };
      if (statusFilter) query.status = statusFilter;
      if (collectionFilter) query.family = collectionFilter;
      if (dateRange?.[0]) query.startDate = dateRange[0].format('YYYYMMDD');
      if (dateRange?.[1]) query.endDate = dateRange[1].format('YYYYMMDD');

      const response = await listInquiries(query);
      setRawData(response?.data || []);
      setTotal(response?.total || (response?.data || []).length);
    } catch {
      setRawData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [auth?.userId, page, pageSize, statusFilter, collectionFilter, dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Apply tag/store name resolution at render time so changes to tagLookup
  // don't trigger a refetch
  const data = useMemo(() => {
    const stores = auth?.stores || [];
    return rawData.map(item => transformInquiry(item, { stores, tags: tagLookup }));
  }, [rawData, auth?.stores, tagLookup]);

  // Wrap filter setters to reset page atomically (avoids stale-page race condition)
  const handleStatusChange = useCallback(value => {
    setStatusFilter(value);
    setPage(1);
  }, []);
  const handleCollectionChange = useCallback(value => {
    setCollectionFilter(value);
    setPage(1);
  }, []);
  const handleDateRangeChange = useCallback(value => {
    setDateRange(value);
    setPage(1);
  }, []);

  const columns = [
    {
      title: 'Name',
      dataIndex: 'clientName',
      key: 'clientName',
      sorter: (a, b) => a.clientName.localeCompare(b.clientName),
      render: (text, record) => (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <span
          style={{ color: '#1677ff', cursor: 'pointer' }}
          onClick={() => history.push(`/Clients?client=${record.clientId}&clientTab=rolex`)}
        >
          {text}
        </span>
      ),
    },
    {
      title: 'Inquiry Date',
      dataIndex: 'inquiryDate',
      key: 'inquiryDate',
      sorter: (a, b) => (a.inquiryDateRaw || '').localeCompare(b.inquiryDateRaw || ''),
      render: (text, record) => (
        <div>
          <div style={{ fontSize: 14 }}>{text}</div>
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>{record.durationMonths}</div>
        </div>
      ),
    },
    {
      title: 'Last Inbound',
      dataIndex: 'lastInbound',
      key: 'lastInbound',
      render: (text, record) => (
        <div>
          <div style={{ fontSize: 14 }}>{text}</div>
          <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>{record.lastInboundDuration}</div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: value => {
        const label = { WAITING: 'Waiting', ORDERED: 'Ordered', CANCELLED: 'Cancelled', FULFILLED: 'Fulfilled' };
        return label[value] || value;
      },
    },
    {
      title: 'Product',
      dataIndex: 'productName',
      key: 'productName',
      render: (text, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {record.productImage && (
            <img
              src={record.productImage}
              alt={record.productMpn}
              style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 4, flexShrink: 0 }}
            />
          )}
          <div>
            {record.productVendorItemId && rolexVendorId ? (
              // eslint-disable-next-line jsx-a11y/no-static-element-interactions
              <div
                style={{ color: '#1677ff', cursor: 'pointer' }}
                onClick={() =>
                  history.push(`/Products?vendorId=${rolexVendorId}&productId=${record.productVendorItemId}`)
                }
              >
                {text}
              </div>
            ) : (
              <div>{text}</div>
            )}
            <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.5)' }}>
              {record.productMpn}
              {record.productCount > 1 && (
                <span style={{ color: 'rgba(0,0,0,0.35)', fontStyle: 'italic' }}>
                  {' '}
                  +{record.productCount - 1} more
                </span>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'edit',
      width: 80,
      render: (_, record) => (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions
        <span style={{ color: '#1677ff', cursor: 'pointer' }} onClick={() => setEditingInquiry(record)}>
          Edit
        </span>
      ),
    },
  ];

  return (
    <StandardLayoutV2
      title="Rolex Inquiries"
      breadcrumb={[{ path: '/', title: 'Today' }, { title: 'Rolex Inquiries' }]}
      classProps="flex-col-left"
      styleProps={{ justifyContent: 'flex-start' }}
      containerStyles={{ backgroundColor: 'transparent', paddingTop: '0px' }}
    >
      <div style={{ display: 'flex', gap: 15, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <Select
          style={{ width: 170, textTransform: 'capitalize' }}
          popupClassName="all-rolex-inquiries-collection-popup"
          value={collectionFilter}
          onChange={handleCollectionChange}
          options={collectionOptions}
          placeholder="All Collections"
          showSearch
          filterOption={(input, option) => (option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
        />
        <Select
          style={{ width: 150 }}
          value={statusFilter}
          onChange={handleStatusChange}
          options={STATUS_OPTIONS}
          placeholder="All Statuses"
        />
        <RangePicker value={dateRange} onChange={handleDateRangeChange} />
      </div>

      <style>
        {'.all-rolex-inquiries-collection-popup .ant5-select-item-option-content { text-transform: capitalize; }'}
      </style>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={data}
          rowKey="id"
          size="middle"
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
            showSizeChanger: true,
          }}
          locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No Rolex inquiries found" /> }}
        />
      )}

      <AddRolexInquiryModal
        open={!!editingInquiry}
        onCancel={() => setEditingInquiry(null)}
        onSave={() => {
          setEditingInquiry(null);
          fetchData();
        }}
        inquiry={editingInquiry}
        vendorId={rolexVendorId}
      />
    </StandardLayoutV2>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
});

export default connect(mapStateToProps)(AllRolexInquiries);
